import { useState } from 'react'

import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { Controller, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  type BoxProps,
  FieldErrorText as EcFieldErrorText,
  FieldRoot,
  Flex,
  HStack,
  Input,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  SecondaryButton,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronRightRegular,
  FaChevronUpRegular,
  FaPlusRegular,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { StepIconButton } from '~/pages/AdminWorkflowTemplates/components/StepIconButton'
import { SubStepEditor } from '~/pages/AdminWorkflowTemplates/components/SubStepEditor'
import { BIG_STEP_TITLE_PRESETS } from '~/pages/AdminWorkflowTemplates/constants/presets'
import type { ApprovalProcesses } from '~/pages/AdminWorkflowTemplates/models/approvalBadge'
import { DEFAULT_APPROVAL_SETTING } from '~/pages/AdminWorkflowTemplates/models/approvalSetting'
import type { WorkflowTemplateFormValues } from '~/pages/AdminWorkflowTemplates/models/schema'

// chakra v3.29 + React 18 types の既知不具合で `FieldErrorTextProps`
// (= `extends HTMLChakraProps<...>, UnstyledProp`) が `{ unstyled }` のみに collapse し、
// children を含む全 props を JSX で受け取れなくなる。 ecloud 由来の component をそのまま使い、
// 取りこぼした props (children) を補う BoxProps で型付けし直す。
const FieldErrorText = EcFieldErrorText as React.FC<BoxProps>

type Props = {
  control: Control<WorkflowTemplateFormValues>
  errors: FieldErrors<WorkflowTemplateFormValues>
  watch: UseFormWatch<WorkflowTemplateFormValues>
  /** リンク先の変更で承認フロー設定を書き換えるため SubStepEditor に渡す */
  getValues: UseFormGetValues<WorkflowTemplateFormValues>
  setValue: UseFormSetValue<WorkflowTemplateFormValues>
  /** 担当者一覧（SubStepEditor に渡す） */
  members: Member[]
  /** 承認フローバッジ算出用の全プロセス設定（SubStepEditor に渡す）。未取得時は undefined。 */
  approvalProcesses?: ApprovalProcesses
}

const CUSTOM_VALUE = '__custom__'

export const BigStepEditor = ({
  control,
  errors,
  watch,
  getValues,
  setValue,
  members,
  approvalProcesses,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: 'bigSteps',
  })

  /**
   * 「その他 (自由入力)」 モードを明示的に保持する field.id の集合。
   * 表示判定を form 値からの導出だけに頼ると、 title が空 / プリセット値の状態で
   * 「その他」 を選んでも form 値が custom を表現できず入力欄が出せない
   * (getTitleSelectValue が CUSTOM_VALUE を返さない)。 useFieldArray の field.id は
   * swap / remove を跨いで安定するため、 これを key に custom モードを持つ。
   */
  const [customTitleIds, setCustomTitleIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  const enterCustomTitleMode = (id: string): void => {
    setCustomTitleIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  /**
   * 折りたたみ中の大ステップ (#5317「アコーディオンなのに開閉の ▼ が無い」)。
   * 既定は全て展開。 `customTitleIds` と同じ理由で **index ではなく field.id** を key に
   * 持つ — 並べ替え / 削除をしても折りたたみ状態が別のステップへ移らない
   * (design `admin/case-templates.html` も折りたたみ状態を並べ替え・削除に追従させる)。
   */
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  /** 開閉トグルのボタン名。 `aria-label` と DS `Tooltip` で同じ文言を使う。 */
  const toggleLabel = (id: string, bigIndex: number): string =>
    collapsedIds.has(id)
      ? t('page:adminWorkflowTemplates.bigStep.expand', { index: bigIndex + 1 })
      : t('page:adminWorkflowTemplates.bigStep.collapse', {
          index: bigIndex + 1,
        })

  const toggleCollapsed = (id: string): void => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const exitCustomTitleMode = (id: string): void => {
    setCustomTitleIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const bigStepsValue = watch('bigSteps') ?? []
  const bigStepTitles = bigStepsValue.map((b) => b.title ?? '')

  const handleAddBigStep = (): void => {
    append({
      displayOrder: fields.length + 1,
      title: '',
      subSteps: [
        {
          displayOrder: 1,
          name: '',
          description: undefined,
          linkedPageKind: undefined,
          assigneeMemberIds: [],
          approvalSetting: DEFAULT_APPROVAL_SETTING,
        },
      ],
    })
  }

  const handleSwap = (a: number, b: number): void => {
    swap(a, b)
  }

  const handleRemoveBigStep = (index: number): void => {
    remove(index)
  }

  /**
   * タイトルセレクトの value を判定する。
   * プリセット値なら title 文字列、カスタムなら '__custom__'、未選択なら '' を返す。
   */
  const getTitleSelectValue = (title: string): string => {
    if (title === '') return ''
    if (BIG_STEP_TITLE_PRESETS.some((p) => p.title === title)) return title
    return CUSTOM_VALUE
  }

  return (
    <VStack align="stretch" gap={3}>
      {fields.map((field, bigIndex) => {
        const bigErrors = errors.bigSteps?.[bigIndex]

        return (
          // ct-bigstep-editor (border-low / radius-base)
          <Box
            key={field.id}
            overflow="hidden"
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="base"
          >
            {/* ct-bigstep-head: 番号バッジ + タイトル select + 上/下/削除 */}
            <HStack
              align="center"
              gap={2}
              background="Surface.secondary"
              paddingX="12px"
              paddingY="8px"
            >
              {/* 開閉トグル (#5317)。
                  ⚠ head 全体をクリック対象にしない — head には大ステップ名の select と
                  ↑↓/削除ボタンが同居しており、 それらのクリックで畳まれると編集ができなく
                  なる (design `case-templates.html` に同じ注意書きがある)。 トグルは
                  この icon-only ボタン 1 個だけが持つ。
                  chevron は right 向きを 90deg 回して down にする (design と同じ絵・同じ
                  回転)。 開閉が状態遷移だと分かるよう `aria-expanded` を持たせる。 */}
              <StepIconButton
                ariaExpanded={!collapsedIds.has(field.id)}
                label={toggleLabel(field.id, bigIndex)}
                onClick={() => {
                  toggleCollapsed(field.id)
                }}
              >
                <FaChevronRightRegular
                  boxSize="16px"
                  style={{
                    transition: 'transform 150ms ease',
                    transform: collapsedIds.has(field.id)
                      ? 'rotate(0deg)'
                      : 'rotate(90deg)',
                  }}
                />
              </StepIconButton>

              {/* 番号バッジ (22px 円, accent-primary) */}
              {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design
                  .ct-bigstep-num-badge (case-templates.html:39) = sm/700。
                  数値 circle はバッジ系トラックで是正予定 */}
              <Flex
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                width="22px"
                height="22px"
                color="Text.highEmphasisInverse"
                fontSize="xs"
                fontWeight="bold"
                background="Surface.accentPrimary"
                borderRadius="full"
              >
                {bigIndex + 1}
              </Flex>

              {/* タイトル select (+ その他自由入力) */}
              <FieldRoot flex={1} invalid={bigErrors?.title !== undefined}>
                <Controller
                  control={control}
                  name={`bigSteps.${bigIndex}.title`}
                  render={({ field: f }) => {
                    const selectVal = customTitleIds.has(field.id)
                      ? CUSTOM_VALUE
                      : getTitleSelectValue(f.value ?? '')
                    return (
                      <HStack align="center" gap={2} width="100%">
                        <NativeSelectRoot
                          flexShrink={0}
                          width="200px"
                          size="sm"
                        >
                          <NativeSelectField
                            onChange={(e) => {
                              const val = e.target.value
                              if (val === CUSTOM_VALUE) {
                                // custom モードにして入力欄を表示。 直前のプリセット値は
                                // クリアし、 自由入力を空から始める (title が残ると
                                // SubStepEditor が推奨リンク先を出し続け AC-05 に反する)。
                                enterCustomTitleMode(field.id)
                                f.onChange('')
                              } else {
                                // '' (未選択) / プリセット値は form 値に反映し
                                // custom モードを解除する
                                exitCustomTitleMode(field.id)
                                f.onChange(val)
                              }
                            }}
                            value={selectVal}
                          >
                            <option value="">
                              {t(
                                'page:adminWorkflowTemplates.bigStep.titleSelectPlaceholder',
                              )}
                            </option>
                            {BIG_STEP_TITLE_PRESETS.map((p) => (
                              <option key={p.title} value={p.title}>
                                {localizeBuiltinWorkflowText(p.title, t)}
                              </option>
                            ))}
                            <option value={CUSTOM_VALUE}>
                              {t(
                                'page:adminWorkflowTemplates.bigStep.titleSelectCustom',
                              )}
                            </option>
                          </NativeSelectField>
                          <NativeSelectIndicator />
                        </NativeSelectRoot>
                        {selectVal === CUSTOM_VALUE && (
                          <Input
                            {...f}
                            flex={1}
                            aria-label={t(
                              'page:adminWorkflowTemplates.bigStep.titleInputPlaceholder',
                            )}
                            id={`bigStep-title-${bigIndex}`}
                            // AC-WFT-008: 「その他」 自由入力は最大 20 文字
                            maxLength={20}
                            placeholder={t(
                              'page:adminWorkflowTemplates.bigStep.titleInputPlaceholder',
                            )}
                            size="sm"
                          />
                        )}
                      </HStack>
                    )
                  }}
                />
                <FieldErrorText>{bigErrors?.title?.message}</FieldErrorText>
              </FieldRoot>

              {/* 上 / 下 / 削除。 40px (入力欄と同じ高さ)。 サブステップ側と同寸。
                  Tooltip と disabled の時の span 包みは StepIconButton が内側で持つ。 */}
              <StepIconButton
                disabled={bigIndex === 0}
                label={t('common:action.moveUp')}
                onClick={() => {
                  handleSwap(bigIndex, bigIndex - 1)
                }}
              >
                <FaChevronUpRegular boxSize="16px" />
              </StepIconButton>
              <StepIconButton
                disabled={bigIndex === fields.length - 1}
                label={t('common:action.moveDown')}
                onClick={() => {
                  handleSwap(bigIndex, bigIndex + 1)
                }}
              >
                <FaChevronDownRegular boxSize="16px" />
              </StepIconButton>
              <StepIconButton
                danger
                label={t('common:action.delete')}
                onClick={() => {
                  handleRemoveBigStep(bigIndex)
                }}
              >
                <FaXmarkRegular boxSize="16px" />
              </StepIconButton>
            </HStack>

            {/* ct-bigstep-body: サブステップ。 折りたたみ中は **display:none で隠す**
                (design `.ct-bigstep-editor.is-collapsed .ct-bigstep-body { display:none }`
                と同じ)。
                ⚠ 条件付きレンダリングで unmount してはいけない — `SubStepEditor` の
                担当者 multi-select は「指定なし」 (UNASSIGNED_SENTINEL) の選択を
                **local state にだけ**持つ (RHF 値からは意図的に除外している) ため、
                unmount すると畳んで開き直した時に chip が消えて未設定に見える。
                form 登録値は shouldUnregister 無効で残るが、 この 1 つだけは form から
                導出できない。 */}
            <VStack
              align="stretch"
              gap={2}
              display={collapsedIds.has(field.id) ? 'none' : 'flex'}
              padding="12px"
            >
              <SubStepEditor
                approvalProcesses={approvalProcesses}
                bigStepIndex={bigIndex}
                // custom モード中は入力値がプリセット文字列と一致しても推奨を
                // 出さない (AC-05)。 title 文字列ではなく custom モードの有無で判定。
                bigStepTitle={
                  customTitleIds.has(field.id)
                    ? undefined
                    : bigStepTitles[bigIndex]
                }
                control={control}
                errors={errors}
                getValues={getValues}
                members={members}
                setValue={setValue}
              />
            </VStack>
          </Box>
        )
      })}

      {/* 大ステップ追加 (design .ct-add-bigstep: btn-secondary, 全幅) */}
      <SecondaryButton width="100%" onClick={handleAddBigStep}>
        <FaPlusRegular />
        {t('page:adminWorkflowTemplates.action.addBigStep')}
      </SecondaryButton>
    </VStack>
  )
}
